import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Leaf, Heart, Star, Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import Ribbon from "../components/Ribbon";
import FloatingMacarons from "../components/FloatingMacarons";
import { supabase } from "../supabaseClient";

const HERO_MACARONS = [
  { top: "4%",  left: "2%",  delay: 0.2, size: 52 },
  { top: "30%", left: "8%",  delay: 0.8, size: 44 },
  { top: "58%", left: "2%",  delay: 0.4, size: 52 },
  { top: "84%", left: "8%",  delay: 0.9, size: 44 },
  { top: "4%",  left: "84%", delay: 0.6, size: 48 },
  { top: "30%", left: "92%", delay: 0.1, size: 56 },
  { top: "58%", left: "84%", delay: 0.7, size: 44 },
  { top: "84%", left: "92%", delay: 1.0, size: 48 },
];

const CONTENT_MACARONS = [
  { top: "4%",  left: "2%",  delay: 0.2, size: 52 },
  { top: "20%", left: "8%",  delay: 0.8, size: 44 },
  { top: "36%", left: "2%",  delay: 0.4, size: 52 },
  { top: "52%", left: "8%",  delay: 0.9, size: 44 },
  { top: "68%", left: "2%",  delay: 0.5, size: 48 },
  { top: "84%", left: "8%",  delay: 1.0, size: 44 },
  { top: "4%",  left: "84%", delay: 0.6, size: 48 },
  { top: "20%", left: "92%", delay: 0.1, size: 56 },
  { top: "36%", left: "84%", delay: 0.7, size: 44 },
  { top: "52%", left: "92%", delay: 0.3, size: 52 },
  { top: "68%", left: "84%", delay: 1.1, size: 44 },
  { top: "84%", left: "92%", delay: 0.8, size: 48 },
];

// ── Static fallback shown when no approved reviews exist ──────
const FALLBACK = {
  name: "Stewart's Sweet Shells",
  rating: 5,
  comment: "Every macaron is handcrafted with care using real ingredients. No preservatives, no shortcuts.",
  isFallback: true,
};

// ── Review Carousel ───────────────────────────────────────────
function ReviewCarousel({ reviews }) {
  const items = reviews.length > 0 ? reviews : [FALLBACK];
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = (next) => {
    setFading(true);
    setTimeout(() => {
      setIndex((next + items.length) % items.length);
      setFading(false);
    }, 300);
  };

  // Auto-rotate every 4 seconds
  useEffect(() => {
    if (items.length <= 1) return;
    const interval = setInterval(() => goTo(index + 1), 4000);
    return () => clearInterval(interval);
  }, [index, items.length]);

  const current = items[index];

  return (
    <div className="max-w-xl mx-auto">
      <div className="card p-8 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sage-300 via-cream-300 to-sage-300" />

        {/* Review content */}
        <div
          className="transition-opacity duration-300"
          style={{ opacity: fading ? 0 : 1 }}
        >
          <p className="font-display text-xl text-sage-800 italic mb-3 leading-relaxed">
            "{current.comment}"
          </p>
          <p className="font-handwrite text-sage-500 text-lg">
            — {current.name}
          </p>
          <div className="flex justify-center gap-1 mt-4">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={16}
                className={i < current.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"}
              />
            ))}
          </div>
        </div>

        {/* Arrow controls — only show if more than 1 review */}
        {items.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => goTo(index - 1)}
              className="w-8 h-8 rounded-full border border-[#9cbd94] flex items-center justify-center text-[#6f9d68] hover:bg-sage-50 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            {/* Dot indicators */}
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === index ? "bg-[#6f9d68] w-4" : "bg-[#9cbd94]"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => goTo(index + 1)}
              className="w-8 h-8 rounded-full border border-[#9cbd94] flex items-center justify-center text-[#6f9d68] hover:bg-sage-50 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Home component ────────────────────────────────────────
export default function Home() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    async function fetchReviews() {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("approved", true)
        .order("created_at", { ascending: false });
      if (!error && data) setReviews(data);
    }
    fetchReviews();
  }, []);

  return (
    <div>
      {/* ── Ribbon at very top ───────────────────────────────── */}
      <div className="pt-24"><Ribbon /></div>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative flex items-center justify-center px-4 py-24 overflow-hidden bg-gradient-to-b from-cream-100 via-cream-50 to-white">
        <div className="absolute top-16 right-8 w-72 h-72 rounded-full bg-sage-100/50 blur-3xl animate-pulse" style={{ animationDuration: "4s" }} />
        <div className="absolute bottom-24 left-8 w-56 h-56 rounded-full bg-cream-300/40 blur-2xl animate-pulse" style={{ animationDuration: "6s" }} />

        <FloatingMacarons positions={HERO_MACARONS} heroOnly />

        <div
          className="relative max-w-2xl mx-auto text-center py4 transition-all duration-700"
          style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? "translateY(0)" : "translateY(16px)" }}
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-sage-200 text-sage-700 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6 shadow-sm">
            <Calendar size={15} />
            Pre-Order Today · Pickup Sat-Sun
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-sage-900 leading-tight mb-3 whitespace-nowrap">
            <span className="text-sage-700">Stewart's</span>{" "}
            <span className="italic text-sage-600">Sweet</span>{" "}
            <span className="text-sage-700">Shells</span>{" "}
          </h1>

          <p className="font-handwrite text-2xl text-sage-400 mb-4">
            Gluten-free Macarons
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
            <Link
              to="/menu"
              className="btn-primary inline-flex items-center justify-center gap-2 text-base px-8 py-4 hover:scale-105 transition-transform"
            >
              <ShoppingBag size={18} />
              Browse Menu
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
            {[
              { icon: <Star size={13} className="text-amber-400 fill-amber-400" />, label: "Made to order" },
              { icon: <Leaf size={13} className="text-sage-500" />, label: "Always gluten free" },
              { icon: <Heart size={13} className="text-red-400 fill-red-400" />, label: "Cottage kitchen" },
            ].map(({ icon, label }) => (
              <span key={label} className="flex items-center gap-1.5 text-xs text-gray-500 font-body">
                {icon} {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section className="bg-sage-800 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl text-cream-100">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            {[
              { title: "Pre-Order Online",   desc: "Browse our menu, choose your flavors and quantities, and add to cart. Order anytime Monday through Friday.", step: "1" },
              { title: "Pay Securely",        desc: "Checkout as a guest — no account needed. Payment is handled by Stripe, fully encrypted and secure.",        step: "2" },
              { title: "Pickup",              desc: "We'll email your confirmed pickup window. Come grab your fresh-baked macarons Saturday or Sunday!",         step: "3" },
            ].map(({ title, desc, step }, i, arr) => (
              <div key={step} className="flex flex-col items-center text-center relative">
                <div className="w-20 h-20 rounded-full border-2 border-sage-500 flex items-center justify-center mb-5">
                  <span className="font-display text-4xl font-bold text-cream-200">{step}</span>
                </div>
                <h3 className="font-display text-lg text-cream-100 mb-2">{title}</h3>
                <p className="text-sage-300 text-sm leading-relaxed max-w-xs">{desc}</p>
                {i < arr.length - 1 && (
                  <ChevronRight size={20} className="hidden md:block absolute right-0 top-9 text-sage-500 translate-x-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── From Our Kitchen / Reviews ───────────────────────── */}
      <section className="relative overflow-hidden py-16 px-4 bg-cream-50">
        <FloatingMacarons positions={CONTENT_MACARONS} />
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="font-display text-3xl text-sage-900 mb-2">From Our Kitchen</h2>
          <p className="font-handwrite text-sage-500 text-xl mb-1">Made in Aubrey, Texas</p>
        </div>
        <ReviewCarousel reviews={reviews} />
        <div className="text-center mt-6">
          <Link
            to="/reviews"
            className="text-sm text-[#6f9d68] hover:text-[#2f502a] transition-colors font-semibold"
          >
            LEAVE A REVIEW
          </Link>
        </div>
      </section>
    </div>
  );
}
